import { AuthService } from '@solvace/core/auth/auth.service';
import { AppEnvironmentAPIConfig } from 'projects/config/model/environment.api.config.model';

export enum SvcSignalRHub {
  Notification,
  Post,
  CommentManager,
  Site,
  WhyWhy,
  Fishbone,
  Kanban,
  KanbanDefectTag,
  CilComment,
}

export type SvcSignalRHubInfo = {
  apiURL: (apis: AppEnvironmentAPIConfig) => string;
  hubPath: (userId?: string) => string;
  listenerMethodNames: () => string[];
  name: () => string;
};

export const INFO_BY_HUB = {
  [SvcSignalRHub.Notification]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlNotification,
    hubPath: () => '/notificationHub',
    listenerMethodNames: () => ['SendNotification'],
    name: () => 'NotificationSignalR',
  },
  [SvcSignalRHub.CommentManager]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlCommentManager,
    hubPath: () => '/notificationHub',
    listenerMethodNames: () => ['CommentCreated', 'CommentDeleted'],
    name: () => 'NotificationSignalR',
  },
  [SvcSignalRHub.CilComment]: {
    // AB#60317 — CIL comment hub (`Solvace.CIL.API/Hubs/CommentHub.cs`, path const `HubPath`):
    // same host as the CIL API in every environment, only the path changes. The API fans a comment
    // out with `SendAsync(payload.EventType, payload)`, so the client method names ARE the two
    // `CommentIntegrationEventType` constants — note it is `CommentUpserted`, not `CommentCreated`.
    // Delivery is per group (`inspection-{id}`), so a consumer must also invoke `JoinInspection`;
    // `CilCommentsRealtimeService` owns that.
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlCil,
    hubPath: () => '/hubs/comment',
    listenerMethodNames: () => ['CommentUpserted', 'CommentDeleted'],
    name: () => 'CilCommentSignalR',
  },
  [SvcSignalRHub.Post]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlPosts,
    hubPath: () => '/postHub',
    listenerMethodNames: () => ['BroadcastNewPost'],
    name: () => 'PostSignalR',
  },
  [SvcSignalRHub.Site]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlAuthSignalR ?? apis.apiUrlAuth,
    hubPath: (userId) => `/changeSiteHub?userId=${userId}`,
    listenerMethodNames: () => ['BroadcastChangeSiteReceived'],
    name: () => 'PostSiteSignalR',
  },
  [SvcSignalRHub.WhyWhy]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlWhyWhy,
    hubPath: () => '/whyWhyHub',
    listenerMethodNames: () => ['WhyWhyUpdated', 'SendFishbone'],
    name: () => 'WhyWhySignalR',
  },
  [SvcSignalRHub.Fishbone]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlFishbone,
    hubPath: () => '/fishboneHub',
    listenerMethodNames: () => ['SendFishbone'],
    name: () => 'FishboneSignalR',
  },
  [SvcSignalRHub.Kanban]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlActionPlan,
    // TODO: confirm with backend whether the hub expects ?siteId=, ?userId=, or no query param at all.
    hubPath: () => '/kanbanhub',
    listenerMethodNames: () => [
      'UpdatePhasePlanRevamp', // (planId, originPhaseId, destinationPhaseId, destinationRefPhaseId, flDateView, flCalendarView, card?)
      'RefreshCardRevamp',     // (planId)
      'MoveToColumnRevamp',    // (planId, fromColumnId, toColumnId, card?) — kanban-custom moves
      'ResetPagination',       // (phaseId)
      'KanbanGoToNextPage',    // (phaseId, idxPhase, flResetAll)
      'KanbanColumnsChanged',  // (systemId) — admin saved a new kanban-custom layout, observers re-fetch
      'CardCommentAdded',      // (actionPlanUniqueId, newCommentTotalCount?) — user posted a comment, observers refresh totals
    ],
    name: () => 'KanbanSignalR',
  },  
  [SvcSignalRHub.KanbanDefectTag]: {
    apiURL: (apis: AppEnvironmentAPIConfig) => apis.apiUrlActionPlan,
    // TODO: confirm with backend whether the hub expects ?siteId=, ?userId=, or no query param at all.
    hubPath: () => '/defectTagKanbanHub',
    listenerMethodNames: () => [
      'UpdateStatusRevamp',
      'RefreshCardRevamp',
      'MoveToColumnRevamp',
      'ResetPagination',
      'KanbanGoToNextPage',
      'CardCommentAdded',
      'KanbanColumnsChanged',
    ],
    name: () => 'DefectTagKanbanSignalR',
  },
};
